// TopNavLayout.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Icon from "@mui/material/Icon";
import { Avatar, Dropdown } from "flowbite-react";
import { IoIosArrowDown, IoMdRefresh } from "react-icons/io";
import { MdOutlinePayments } from "react-icons/md";
import { AnimatePresence, motion } from "framer-motion";
import { useApplication } from "../contexts/ApplicationContext";
import { permissionChecker, removeToken } from "../utils/permissions";
import { calculateExpirationPeriodTest, formatToReal } from "../utils/utils";
import ModalAddCredit from "./modals/ModalAddCredit";

const getPlanStatus = (daysLeft) => {
  if (!Number.isFinite(daysLeft)) {
    return null;
  }

  if (daysLeft < 0) {
    return {
      label: "Expirado",
      title: "Plano expirado",
      tone: "border-rose-400/20 bg-rose-400/10 text-rose-100",
    };
  }

  if (daysLeft === 0) {
    return {
      label: "Hoje",
      title: "Expira hoje",
      tone: "border-amber-400/20 bg-amber-400/10 text-amber-100",
    };
  }

  if (daysLeft <= 7) {
    return {
      label: `${daysLeft}d`,
      title: `${daysLeft} dias restantes`,
      tone: "border-amber-400/20 bg-amber-400/10 text-amber-100",
    };
  }

  if (daysLeft <= 30) {
    return {
      label: `${daysLeft}d`,
      title: `${daysLeft} dias restantes`,
      tone: "border-white/15 bg-white/6 text-white/75",
    };
  }

  return {
    label: `${daysLeft}d`,
    title: `${daysLeft} dias restantes`,
    tone: "border-emerald-400/20 bg-emerald-400/10 text-emerald-100",
  };
};

export default function TopNavLayout() {
  const { user, userPermission, refetchUser } = useApplication();
  const location = useLocation();

  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [expandedMobile, setExpandedMobile] = useState({});
  const [isOpenModalAddCredit, setIsOpenModalAddCredit] = useState(false);
  const [titlePage, setTitlePage] = useState("Dashboard");

  const daysLeft = user?.Company?.planExpirationDate
    ? calculateExpirationPeriodTest(user?.Company?.planExpirationDate)
    : null;
  const planStatus = getPlanStatus(daysLeft);
  const companyName =
    user?.Company?.name ||
    user?.Company?.companyName ||
    user?.email?.split("@")[0] ||
    "Conta";

  const logoSrc = "/assets/logo-white.png";

  const menuSections = useMemo(
    () => [
      {
        id: "dashboard",
        type: "single",
        name: "Dashboard",
        icon: "equalizer",
        items: [
          {
            name: "Dashboard",
            link: "/dashboard",
            icon: "equalizer",
            permission: ["USER", "USER_CALENDARS"],
            typeCompany: ["ALL"],
          },
        ],
      },
      {
        id: "ampliahub",
        type: "single",
        name: "Amplia Hub",
        icon: "assignment_ind",
        items: [
          {
            name: "Amplia Hub",
            link: "/crm",
            icon: "assignment_ind",
            permission: ["USER"],
            typeCompany: ["ALL"],
          },
        ],
      },
      {
        name: "Tarefas",
        type: "single",
        link: "/crm/tasks",
        icon: "pending_actions",
        items: [
          {
            name: "Tarefas",
            link: "/crm/tasks",
            icon: "pending_actions",
            permission: ["USER"],
            typeCompany: ["ALL"],
          },
        ],
      },
      {
        id: "operacoes",
        type: "group",
        name: "Operações",
        icon: "sync_alt",
        items: [
          {
            name: "Consultas",
            link: "/queries",
            icon: "manage_search",
            permission: ["USER"],
            typeCompany: ["ALL"],
          },
          {
            name: "Histórico de consultas",
            link: "/reports/queries",
            icon: "history",
            permission: ["USER"],
            typeCompany: ["ALL"],
          },
          {
            name: "Simulações",
            link: "/simulations",
            icon: "add_chart",
            permission: ["USER"],
            typeCompany: ["ALL"],
          },
        ],
      },
      {
        id: "crm_clientes",
        type: "single",
        name: "Carteira",
        icon: "assignment_ind",
        items: [
          {
            name: "Leads",
            link: "/admin/leads",
            icon: "assignment_ind",
            permission: ["ADMIN"],
            typeCompany: ["REPRESENTANTE", "ADMIN"],
          },
        ],
      },
      {
        id: "admin",
        type: "group",
        name: "Administração",
        icon: "admin_panel_settings",
        items: [
          {
            name: "Usuários",
            link: "/users",
            icon: "group",
            permission: ["USER"],
            typeCompany: ["ALL"],
          },
          {
            name: "Gestão Financeira",
            link: "/financeiro",
            icon: "account_balance",
            permission: ["POWER_USER"],
            typeCompany: ["CLIENTE"],
          },
          {
            name: "Rotinas e Métricas",
            link: "/admin/routine-metrics",
            icon: "monitoring",
            permission: ["POWER_USER"],
            typeCompany: ["CLIENTE"],
          },
          {
            name: "Área Educacional",
            link: "/admin/area-educacional",
            icon: "school",
            permission: ["USER"],
            typeCompany: ["ALL"],
          },
        ],
      },
    ],
    [],
  );

  useEffect(() => {
    const flat = [];
    menuSections.forEach((section) => {
      (section.items || []).forEach((item) => {
        flat.push({ name: item.name, link: item.link });
      });
    });
    const found = flat.find((f) => f.link === location.pathname);
    setTitlePage(found?.name || "Dashboard");
  }, [location.pathname, menuSections]);

  const visibleSections = useMemo(
    () =>
      menuSections
        .map((section) => ({
          ...section,
          items: (section.items || []).filter((item) =>
            permissionChecker(
              item.permission,
              userPermission,
              user,
              item.typeCompany,
            ),
          ),
        }))
        .filter((section) => section.items.length > 0),
    [menuSections, userPermission, user],
  );

  const isActive = (href) => location.pathname === href;
  const isSectionActive = (section) =>
    section.items.some((item) => isActive(item.link));

  const toggleMobileGroup = (id) =>
    setExpandedMobile((prev) => ({ ...prev, [id]: !prev[id] }));

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header className="sticky top-0 z-40 border-b border-white/10 bg-[linear-gradient(180deg,rgba(10,10,10,0.96),rgba(5,5,5,0.92))] backdrop-blur-xl mb-4 shadow-sm">
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />
          <div className="absolute -left-20 top-[-6rem] h-40 w-40 rounded-full bg-white/8 blur-3xl" />
          <div className="absolute right-0 top-[-5rem] h-40 w-40 rounded-full bg-white/6 blur-3xl" />
        </div>

        <div className="relative mx-auto px-3 md:px-5 lg:px-8">
          <div className="flex items-center justify-between gap-3 md:py-0 py-2">
            <div className="flex min-w-0 shrink-0 items-center gap-3">
              <Link to="/dashboard" className="group flex items-center">
                <span className="flex items-center justify-center rounded-2xl">
                  <img
                    src={logoSrc}
                    alt="Logo"
                    className="h-10 w-auto object-contain"
                  />
                </span>
              </Link>

              <button
                className="md:hidden inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/5 p-1.5 text-white/70 transition hover:border-white/20 hover:bg-white/10 hover:text-white"
                onClick={() => setIsMobileOpen((o) => !o)}
                aria-label="Abrir menu"
              >
                <motion.svg
                  animate={isMobileOpen ? "open" : "closed"}
                  className="h-5 w-5"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <motion.path
                    variants={{
                      closed: { d: "M4 6h16M4 12h16M4 18h16" },
                      open: { d: "M6 6l12 12M6 18L18 6" },
                    }}
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    transition={{ duration: 0.2 }}
                  />
                </motion.svg>
              </button>
            </div>

            <nav className="hidden md:flex flex-1 items-center justify-center overflow-visible">
              <div className="flex max-w-full items-center gap-1 rounded-full py-2">
                {visibleSections.map((section) => {
                  const sectionActive = isSectionActive(section);
                  if (section.type === "single") {
                    const item = section.items[0];
                    return (
                      <TopLink
                        key={section.id ?? section.name}
                        to={item.link}
                        icon={section.icon}
                        active={sectionActive}
                        label={section.name}
                      />
                    );
                  }
                  return (
                    <TopDropdown
                      key={section.id}
                      label={section.name}
                      icon={section.icon}
                      items={section.items}
                      isActiveGroup={sectionActive}
                    />
                  );
                })}
              </div>
            </nav>

            <div className="flex items-center gap-2 shrink-0">
              {user?.Company?.typePayment === "PRE" && (
                <div className="hidden md:flex items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-medium text-white/80 shadow-[0_8px_24px_rgba(0,0,0,0.16)]">
                    <Icon className="text-[14px] text-emerald-400">
                      account_balance_wallet
                    </Icon>
                    {formatToReal(user?.Company?.credit || 0)}
                  </span>
                  <IconButton
                    onClick={() => setIsOpenModalAddCredit(true)}
                    title="Adicionar créditos"
                    accent
                  >
                    <MdOutlinePayments size={15} />
                  </IconButton>
                  <IconButton onClick={() => refetchUser()} title="Atualizar">
                    <IoMdRefresh size={15} />
                  </IconButton>
                </div>
              )}

              <Dropdown
                label={
                  <div className="flex items-center gap-2 rounded-full bg-white/10  px-2.5 py-1.5 text-left shadow-[0_8px_24px_rgba(0,0,0,0.16)] transition hover:border-white/20 hover:bg-white/8">
                    <Avatar rounded size="xs" img={user?.imageUrl} />
                    <span className="hidden lg:block max-w-[130px] truncate text-xs font-medium text-white/85">
                      {user?.email?.split("@")[0]}
                    </span>
                    <IoIosArrowDown className="hidden lg:block text-[11px] text-white/35" />
                  </div>
                }
                arrowIcon={false}
                inline
              >
                <Dropdown.Header>
                  <span className="block truncate text-xs font-semibold text-gray-900">
                    {user?.email}
                  </span>
                </Dropdown.Header>

                <Dropdown.Item className="py-0 px-0">
                  <Link
                    to="/profile"
                    className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm"
                  >
                    <Icon className="text-[16px] text-gray-500">
                      manage_accounts
                    </Icon>
                    Meu perfil
                  </Link>
                </Dropdown.Item>

                <Dropdown.Divider />

                <Dropdown.Item
                  onClick={removeToken}
                  className="font-medium text-red-600"
                >
                  Sair
                </Dropdown.Item>
              </Dropdown>
            </div>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {isMobileOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              className="md:hidden fixed inset-0 top-14 z-30 bg-black/70 backdrop-blur-sm"
              onClick={() => setIsMobileOpen(false)}
            />

            <motion.aside
              initial={{ x: "-100%", opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: "-100%", opacity: 0 }}
              transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
              className="md:hidden fixed top-14 left-0 bottom-0 z-40 flex w-[19rem] flex-col overflow-hidden border border-white/10 border-l-0 bg-[#0a0a0a]/98 shadow-[24px_0_48px_rgba(0,0,0,0.42)] backdrop-blur-2xl"
              style={{ borderRadius: "0 1.5rem 1.5rem 0" }}
            >
              <div className="border-b border-white/8 bg-white/4 px-4 py-4">
                <div className="mt-1 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2 className="truncate text-base font-semibold text-white">
                      {titlePage}
                    </h2>
                  </div>
                </div>
              </div>

              <nav className="flex-1 overflow-y-auto px-3 py-3">
                <div className="space-y-3">
                  {visibleSections.map((section) => {
                    const sectionActive = isSectionActive(section);
                    if (section.type === "single") {
                      const item = section.items[0];
                      return (
                        <MobileItem
                          key={section.id ?? section.name}
                          to={item.link}
                          icon={section.icon}
                          active={sectionActive}
                          label={section.name}
                        />
                      );
                    }
                    return (
                      <MobileGroup
                        key={section.id}
                        label={section.name}
                        icon={section.icon}
                        open={!!expandedMobile[section.id]}
                        onToggle={() => toggleMobileGroup(section.id)}
                        childrenItems={section.items}
                        isActiveGroup={sectionActive}
                      />
                    );
                  })}
                </div>
              </nav>

              <div className="border-t border-white/8 px-4 py-4">
                <div className="flex items-center gap-3 rounded-2xl border border-white/40 bg-white/5 p-3">
                  <Avatar rounded size="sm" img={user?.imageUrl} />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-white">
                      {user?.email?.split("@")[0]}
                    </p>
                    <p className="truncate text-xs text-white/40">
                      {user?.email}
                    </p>
                  </div>
                  <button
                    onClick={removeToken}
                    className="rounded-full p-1.5 text-red-400/70 transition hover:bg-red-400/10 hover:text-red-400"
                    title="Sair"
                  >
                    <Icon className="text-[18px]">logout</Icon>
                  </button>
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>
    </>
  );
}

/* ===========================
 * Subcomponentes
 * =========================== */

function IconButton({ children, onClick, title, accent }) {
  return (
    <button
      onClick={onClick}
      title={title}
      className={`inline-flex h-8 w-8 items-center justify-center rounded-full transition ${
        accent
          ? "border border-white/20 bg-white text-[#101010] shadow-[0_10px_20px_rgba(255,255,255,0.12)] hover:bg-white/90"
          : "border border-white/10 bg-white/5 text-white/65 hover:border-white/20 hover:bg-white/10 hover:text-white"
      }`}
    >
      {children}
    </button>
  );
}

function TopLink({ to, icon, label, active }) {
  return (
    <Link
      to={to}
      className={`group relative inline-flex h-10 items-center gap-2 rounded-full px-4 text-sm font-medium transition-all duration-200 ${
        active
          ? "bg-white text-[#101010] shadow-[0_10px_24px_rgba(255,255,255,0.12)]"
          : "text-white/60 hover:bg-white/8 hover:text-white"
      }`}
    >
      <span
        className={`flex h-6 w-6 items-center justify-center rounded-full text-[12px] transition ${
          active
            ? "bg-black/5 text-[#101010]"
            : "bg-white/5 text-white/45 group-hover:bg-white/10 group-hover:text-white"
        }`}
      >
        {icon && <Icon style={{ fontSize: "18px" }}>{icon}</Icon>}
      </span>
      <span className="whitespace-nowrap text-[13px]">{label}</span>
    </Link>
  );
}

function TopDropdown({ label, icon, items, isActiveGroup }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className={`group relative inline-flex h-10 items-center gap-2 rounded-full px-4 text-sm font-medium transition-all duration-200 ${
          isActiveGroup
            ? "bg-white text-[#101010] shadow-[0_10px_24px_rgba(255,255,255,0.12)]"
            : "text-white/60 hover:bg-white/8 hover:text-white"
        }`}
      >
        <span
          className={`flex h-6 w-6 items-center justify-center rounded-full text-[16px] transition ${
            isActiveGroup
              ? "bg-black/5 text-[#101010]"
              : "bg-white/5 text-white/45 group-hover:bg-white/10 group-hover:text-white"
          }`}
        >
          {icon && <Icon style={{ fontSize: "18px" }}>{icon}</Icon>}
        </span>
        <span className="whitespace-nowrap text-[13px]">{label}</span>
        <IoIosArrowDown
          className={`text-[11px] transition-transform duration-200 ${
            open ? "rotate-180" : ""
          } ${isActiveGroup ? "text-[#101010]/60" : "text-white/30"}`}
        />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.97, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: -4 }}
            transition={{ duration: 0.12, ease: "easeOut" }}
            className="absolute left-0 z-50 mt-3 w-64 overflow-hidden rounded-2xl bg-gray-900/95 border border-white/10 p-2 shadow-2xl shadow-black/50 backdrop-blur-xl"
          >
            <div className="px-2 pb-2 pt-1">
              <p className="text-[10px] uppercase tracking-[0.22em] text-white/30">
                {label}
              </p>
            </div>
            <div className="space-y-1">
              {items.map((child) => (
                <DropdownLink
                  key={child.link}
                  item={child}
                  onClose={() => setOpen(false)}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function DropdownLink({ item, onClose }) {
  const location = useLocation();
  const active = location.pathname === item.link;

  return (
    <Link
      to={item.link}
      onClick={onClose}
      className={`group flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm transition-all ${
        active
          ? "border border-white/10 bg-white/10 text-white"
          : "border border-transparent text-white/60 hover:border-white/8 hover:bg-white/6 hover:text-white"
      }`}
    >
      <span
        className={`flex h-7 w-7 items-center justify-center rounded-lg text-[15px] transition ${
          active
            ? "bg-white/10 text-white"
            : "bg-white/5 text-white/40 group-hover:bg-white/10 group-hover:text-white"
        }`}
      >
        {item.icon && <Icon className="text-[15px]">{item.icon}</Icon>}
      </span>
      <span className="min-w-0 truncate font-medium">{item.name}</span>
      {active && (
        <span className="ml-auto h-1.5 w-1.5 shrink-0 rounded-full bg-white" />
      )}
    </Link>
  );
}

function MobileItem({ to, icon, label, active }) {
  return (
    <Link
      to={to}
      className={`group flex items-center gap-3 rounded-2xl border px-3 py-3 text-sm font-medium transition-all ${
        active
          ? "border-white/10 bg-white/10 text-white shadow-[0_10px_24px_rgba(0,0,0,0.16)]"
          : "border-transparent bg-white/0 text-white/60 hover:border-white/8 hover:bg-white/6 hover:text-white"
      }`}
    >
      <span
        className={`flex h-7 w-7 items-center justify-center rounded-lg text-[17px] transition ${
          active
            ? "bg-white/10 text-white"
            : "bg-white/5 text-white/40 group-hover:bg-white/10 group-hover:text-white"
        }`}
      >
        {icon && <Icon className="text-[17px]">{icon}</Icon>}
      </span>
      <span className="min-w-0 truncate">{label}</span>
      {active && (
        <span className="ml-auto h-1.5 w-1.5 shrink-0 rounded-full bg-white" />
      )}
    </Link>
  );
}

function MobileGroup({
  label,
  icon,
  open,
  onToggle,
  childrenItems,
  isActiveGroup,
}) {
  const location = useLocation();

  return (
    <div>
      <button
        onClick={onToggle}
        className={`group flex w-full items-center justify-between rounded-2xl border px-3 py-3 text-sm font-medium transition-all ${
          isActiveGroup
            ? "border-white/10 bg-white/10 text-white shadow-[0_10px_24px_rgba(0,0,0,0.16)]"
            : "border-transparent bg-white/0 text-white/60 hover:border-white/8 hover:bg-white/6 hover:text-white"
        }`}
      >
        <span className="flex items-center gap-3">
          <span
            className={`flex h-7 w-7 items-center justify-center rounded-lg text-[17px] transition ${
              isActiveGroup
                ? "bg-white/10 text-white"
                : "bg-white/5 text-white/40 group-hover:bg-white/10 group-hover:text-white"
            }`}
          >
            {icon && <Icon className="text-[17px]">{icon}</Icon>}
          </span>
          <span className="min-w-0 truncate">{label}</span>
        </span>
        <IoIosArrowDown
          className={`text-[11px] transition-transform duration-200 ${
            open ? "rotate-180" : ""
          } ${isActiveGroup ? "text-white/60" : "text-white/30"}`}
        />
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="ml-4 mt-1.5 space-y-1 border-l border-white/8 pl-3.5 pb-1">
              {(childrenItems || []).map((child) => {
                const active = location.pathname === child.link;
                return (
                  <Link
                    key={child.link}
                    to={child.link}
                    className={`group flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm transition-all ${
                      active
                        ? "border border-white/10 bg-white/10 text-white font-medium"
                        : "border border-transparent text-white/55 hover:border-white/8 hover:bg-white/6 hover:text-white"
                    }`}
                  >
                    <span
                      className={`flex h-6 w-6 items-center justify-center rounded-lg text-[15px] transition ${
                        active
                          ? "bg-white/10 text-white"
                          : "bg-white/5 text-white/40 group-hover:bg-white/10 group-hover:text-white"
                      }`}
                    >
                      {child.icon && (
                        <Icon className="text-[15px]">{child.icon}</Icon>
                      )}
                    </span>
                    <span className="min-w-0 truncate">{child.name}</span>
                  </Link>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
